/**
 * 이상한 방 - 통합 포인터 제스처 인식 (터치가 일급, 마우스는 그 위에 얹힌다)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 7.0절(모바일 브라우저 포함 즉시 접속) · 9.4절(최대 병목은 6명 모으기)
 *
 * ## 왜 Pointer Events 하나로 가는가
 *
 * 아이소메트릭 전환의 목적이 **모바일 접근성**이다. 4주 계획의 진짜 병목은 코딩이 아니라
 * "6명 모으기"이고(9.4절), 링크를 던져 폰에서 바로 들어올 수 있으면 플레이테스트 횟수가 달라진다.
 * 그러므로 이 파일의 기준 입력 장치는 **손가락**이며 마우스는 같은 경로에 얹히는 특수 케이스다.
 *
 * `touchstart`/`mousedown`을 따로 다루면 경로가 둘로 갈라지고, 두 경로는 반드시 어긋난다
 * (모바일 브라우저는 터치 뒤에 300ms 늦은 가짜 마우스 이벤트를 보낸다 - 그게 곧 이중 발사다).
 * Pointer Events는 마우스·터치·펜을 `pointerId` 하나로 통일하므로 분기 자체가 사라진다.
 *
 * ## 탭 판정이 이 파일에서 가장 중요한 한 줄이다
 *
 * 이 게임에서 **오발은 탄환 손실**이다(컨셉시트 4.3절: 오답 1회 = 1발).
 * 화면을 끌어 시점을 옮기다 손을 뗀 것이 탭으로 오인되면 플레이어는 아무것도 겨누지 않고 한 발을 잃는다.
 * 그래서 탭은 두 조건을 **모두** 만족해야 한다.
 *
 *  1. 눌린 지점에서 `tapSlopPx`(기본 10px)를 **한 번도** 벗어나지 않았을 것 - 래치다.
 *     최종 위치만 보면 "끌었다가 제자리로 돌아온" 드래그가 탭으로 통과한다.
 *  2. 두 번째 손가락이 닿은 적이 없을 것 - 핀치 중 한 손가락을 떼는 것은 탭이 아니다.
 *
 * 반대로 **판정을 관대하게 하는 책임은 여기 있지 않다.** 손가락이 굵어서 몇 px 빗나가는 문제는
 * `tapPicker.ts`가 레이캐스트 반경으로 푼다. 여기서는 "탭인가 아닌가"만 정한다.
 *
 * ## `touch-action: none`이 없으면 이 파일은 동작하지 않는다 (중요)
 *
 * 기본 상태의 브라우저는 한 손가락 드래그를 스크롤로, 두 손가락을 페이지 확대로 가로챈다.
 * 가로채는 순간 우리에게는 `pointercancel`만 오고 팬·핀치가 통째로 죽는다.
 * 그래서 캔버스에 **`touch-action: none`이 반드시 걸려 있어야 한다.**
 * CSS에 선언하는 것이 정석이지만, 선언을 빼먹으면 "폰에서만 조작이 안 되는" 재현 어려운 버그가 되므로
 * 이 모듈이 부착 시점에 인라인 스타일로도 걸고 `dispose()`에서 원래 값으로 되돌린다.
 *
 * ## 좌표 규약
 *
 * 콜백이 넘기는 좌표는 전부 **클라이언트 좌표**(`ev.clientX`/`clientY`, CSS 픽셀)다.
 * `tapPicker.pickAt(ctx, x, y, ...)`이 같은 규약을 쓰므로 탭 좌표를 그대로 흘려 넣으면 된다.
 * 요소 기준 좌표로 바꾸지 않는 이유: 변환에는 `getBoundingClientRect()`가 필요한데
 * 그건 레이아웃을 강제로 계산시키는 호출이라 포인터 이동마다 부르면 안 된다.
 */

/* --- 임계값 -------------------------------------------------------------------- */

/**
 * 탭으로 인정되는 최대 이동 거리(CSS px). 넘어가면 그 포인터는 드래그로 확정된다.
 * 10px은 손가락 떨림(보통 2~5px)보다 크고, 의도적인 팬(보통 첫 프레임에 20px+)보다 작다.
 */
const TAP_SLOP_PX = 10

/**
 * 탭으로 인정되는 최대 누름 시간(ms).
 *
 * 이 상한은 "겨누느라 느린 탭"을 거르려는 것이 **아니다** - 그건 관대하게 통과시켜야 한다.
 * 폰을 쥔 손가락이 화면에 얹혔다가 떨어지는 것처럼 **누를 의도가 없던 접촉**만 걸러내려는 값이라
 * 넉넉하게 잡는다. 오발(탄환 손실)보다 미발사(다시 누르면 그만)가 싸지만,
 * 여기서 인색하게 굴면 "정성껏 겨눴는데 안 나갔다"가 되어 그것도 나름 최악이다.
 */
const TAP_MAX_DURATION_MS = 600

/** 팬으로 인정되는 포인터 개수. 두 손가락은 줌·회전 전용이다. */
const PAN_POINTER_COUNT = 1

/** 핀치·트위스트에 필요한 포인터 개수. */
const PINCH_POINTER_COUNT = 2

/* --- 휠 → 핀치 매핑 ------------------------------------------------------------ */

/** `deltaMode = DOM_DELTA_LINE`일 때 한 줄의 픽셀 환산값. 브라우저가 실제 행 높이를 주지 않는다. */
const WHEEL_LINE_HEIGHT_PX = 16

/** `deltaMode = DOM_DELTA_PAGE`일 때 한 페이지의 픽셀 환산값(요소 높이를 모를 때의 폴백). */
const WHEEL_PAGE_HEIGHT_PX = 400

/**
 * 휠 이벤트 하나가 낼 수 있는 최대 이동량(px).
 * 트랙패드 관성 스크롤은 한 이벤트에 300px 이상을 보내기도 하는데, 그대로 지수에 넣으면
 * 한 번의 튕김에 전체 조망에서 최대 확대까지 날아간다. 한 노치(≈120px)를 상한으로 둔다.
 */
const WHEEL_MAX_STEP_PX = 120

/**
 * 휠 이동량 → 배율 지수. 한 노치(120px)가 약 1.20배/0.84배가 된다.
 * 지수를 쓰는 이유는 줌이 곱셈 공간이기 때문이다 - 뺄셈으로 다루면 확대 상태에서만 반응이 둔해진다.
 */
const WHEEL_ZOOM_EXPONENT = 0.0015

/** 배율 1은 "변화 없음"이다. 콜백을 부르지 않는다. */
const NO_SCALE_CHANGE = 1

/* --- 트위스트 → 회전 요청 ------------------------------------------------------- */

const RADIANS_PER_DEGREE = Math.PI / 180
const FULL_TURN_RADIANS = Math.PI * 2
const HALF_TURN_RADIANS = Math.PI

/**
 * 회전 한 단계를 발행하는 트위스트 각도.
 *
 * 카메라 회전은 90° 단위 이산 동작이라 되돌리는 비용이 크다(애니메이션 + 방향 재인식).
 * 그래서 핀치 중에 섞여 들어오는 각도 잡음(보통 5~15°)보다 확실히 큰 값을 쓴다.
 */
const TWIST_STEP_DEGREES = 35
const TWIST_STEP_RADIANS = TWIST_STEP_DEGREES * RADIANS_PER_DEGREE

/**
 * 키보드 회전. **`ev.key`가 아니라 `ev.code`로 본다** -
 * 한글 입력 상태에서 `key`는 'ㅂ'/'ㄷ'이 되어 조작이 통째로 죽는다.
 */
const ROTATE_KEY_COUNTERCLOCKWISE = 'KeyQ'
const ROTATE_KEY_CLOCKWISE = 'KeyE'

/** 키 입력을 무시할 대상. HUD 입력칸에 타이핑하다 방이 돌아가면 안 된다. */
const EDITABLE_TAG_NAMES: ReadonlySet<string> = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/* --- 공개 타입 ----------------------------------------------------------------- */

/**
 * 제스처 구독 핸들.
 *
 * `on*`은 구독 **추가**다(마지막 것이 이기는 대입이 아니다). 핸들러를 두 번 등록하면 두 번 불린다.
 * 개별 해제는 제공하지 않는다 - 이 게임에서 제스처 소비자는 카메라·판정 두 곳뿐이고 수명이
 * 화면 수명과 같기 때문이다. 전량 해제는 `dispose()`가 담당한다.
 */
export type PointerGestures = {
  /** 탭. 좌표는 **누른 지점**의 클라이언트 좌표다(뗀 지점이 아니다 - 조준 의도는 닿는 순간 정해진다). */
  onTap(cb: (x: number, y: number) => void): void
  /** 한 손가락(또는 마우스 버튼) 드래그. 슬롭을 넘긴 뒤부터 CSS px 델타를 흘린다. */
  onPanDelta(cb: (dx: number, dy: number) => void): void
  /**
   * 한 손가락이 눌린 채 움직였다. **슬롭 전에도 흘린다** - 놓을 자리 미리보기는 손가락이 닿는 순간부터 따라와야
   * "정확히 놓기"가 되고, 10px을 기다리면 짧은 조준이 미리보기 없이 끝난다. 팬과 달리 **절대 좌표**다
   * (겨눈 자리는 누적 델타가 아니라 지금 이 점이다).
   *
   * 팬과 함께 나간다 - 둘 중 무엇을 쓸지는 받는 쪽이 정한다(`app/inputLayer.ts`: 놓는 중이면 미리보기, 아니면 팬).
   * 여기서 고르면 이 파일이 페이즈를 알게 된다.
   */
  onAimMove(cb: (x: number, y: number) => void): void
  /**
   * 한 손가락 드래그가 **탭이 아닌 채로** 끝났다. 좌표는 뗀 지점이다.
   * 끌어서 겨누고 떼어 놓는 제스처의 마침표라 `onTap`과 배타적이다 - 탭이었으면 이쪽은 오지 않는다.
   */
  onAimEnd(cb: (x: number, y: number) => void): void
  /** 핀치 줌 또는 휠. `scaleDelta`는 **비율**(1 = 변화 없음, >1 = 확대), 중심은 클라이언트 좌표. */
  onPinchZoom(cb: (scaleDelta: number, centerX: number, centerY: number) => void): void
  /** 90° 회전 요청. 두 손가락 트위스트 또는 Q/E 키에서 온다. */
  onRotateRequest(cb: (delta: 1 | -1) => void): void
  dispose(): void
}

/**
 * 조정 가능한 값. 전부 선택이므로 계약 시그니처 `createPointerGestures(el)`는 그대로 유효하다.
 * `now`가 있는 이유는 테스트에서 롱프레스를 실제로 기다리지 않기 위해서다.
 */
export type PointerGestureOptions = Readonly<{
  now?: () => number
  tapSlopPx?: number
  tapMaxDurationMs?: number
  /** `touch-action: none`을 인라인으로 걸지 여부. CSS에서 이미 보장한다면 false로 둘 수 있다. */
  manageTouchAction?: boolean
}>

/* --- 내부 상태 ----------------------------------------------------------------- */

/**
 * 추적 중인 포인터 하나. 도메인 상태가 아니라 입력 장치의 순간 상태다.
 * 갱신은 제자리 변경이 아니라 교체로 한다(`editor.ts`의 `DragTracker`와 같은 규약).
 */
type TrackedPointer = Readonly<{
  id: number
  startX: number
  startY: number
  startTimeMs: number
  x: number
  y: number
  /** 아직 탭으로 인정될 수 있는가. 한 번 false가 되면 이 포인터가 뗄 때까지 되돌아오지 않는다. */
  isTapCandidate: boolean
  /** 슬롭을 넘겨 팬이 시작됐는가. 넘기기 전의 미세 떨림은 카메라로 보내지 않는다. */
  isPanning: boolean
}>

/** 두 포인터 사이의 직전 관계. 배율·트위스트는 전부 이 차분에서 나온다. */
type PinchTracker = Readonly<{ distance: number; angle: number }>

type Listener<Args extends readonly unknown[]> = (...args: Args) => void

type Signal<Args extends readonly unknown[]> = Readonly<{
  add: (cb: Listener<Args>) => void
  emit: (...args: Args) => void
  clear: () => void
}>

function createSignal<Args extends readonly unknown[]>(): Signal<Args> {
  const listeners: Listener<Args>[] = []

  return {
    add: (cb) => {
      listeners.push(cb)
    },
    emit: (...args) => {
      for (const cb of listeners) cb(...args)
    },
    clear: () => {
      listeners.length = 0
    },
  }
}

/* --- 순수 헬퍼 ----------------------------------------------------------------- */

/** 두 각도의 최단 차이(-π ~ π). 트위스트가 ±180°를 넘을 때 값이 튀는 것을 막는다. */
function shortestAngleDelta(from: number, to: number): number {
  const raw = (to - from) % FULL_TURN_RADIANS
  if (raw > HALF_TURN_RADIANS) return raw - FULL_TURN_RADIANS
  if (raw < -HALF_TURN_RADIANS) return raw + FULL_TURN_RADIANS
  return raw
}

/**
 * 휠 이동량을 픽셀로 정규화한다.
 * `deltaMode`를 무시하면 Firefox(줄 단위)에서 줌이 1/16로 느려지는 고전 버그가 난다.
 */
function wheelDeltaPixels(ev: WheelEvent, element: HTMLElement): number {
  if (ev.deltaMode === WheelEvent.DOM_DELTA_LINE) return ev.deltaY * WHEEL_LINE_HEIGHT_PX
  if (ev.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return ev.deltaY * (element.clientHeight > 0 ? element.clientHeight : WHEEL_PAGE_HEIGHT_PX)
  }
  return ev.deltaY
}

/**
 * 휠 → 핀치 배율.
 * 위로 굴리면(deltaY < 0) 확대다 - 지도·사진 앱의 보편 규약이라 배울 필요가 없다.
 */
export function wheelScaleDelta(ev: WheelEvent, element: HTMLElement): number {
  const raw = wheelDeltaPixels(ev, element)
  if (!Number.isFinite(raw)) return NO_SCALE_CHANGE

  const clamped = Math.max(-WHEEL_MAX_STEP_PX, Math.min(WHEEL_MAX_STEP_PX, raw))
  return Math.exp(-clamped * WHEEL_ZOOM_EXPONENT)
}

/**
 * 키 입력을 무시해야 하는 대상인가.
 * `instanceof HTMLElement`를 쓰지 않는 이유: 이 함수는 jsdom·iframe 등 다른 realm의 노드를 받을 수 있고,
 * 그때 instanceof는 조용히 false가 되어 "가끔 입력칸에서 방이 돌아가는" 버그가 된다.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (target === null) return false

  const probe = target as { tagName?: unknown; isContentEditable?: unknown }
  if (probe.isContentEditable === true) return true

  return typeof probe.tagName === 'string' && EDITABLE_TAG_NAMES.has(probe.tagName.toUpperCase())
}

/* --- 제스처 인식기 -------------------------------------------------------------- */

/**
 * 요소에 제스처 인식기를 붙인다. 정리는 반환된 핸들의 `dispose()`로만 한다.
 *
 * 리스너 부착 위치가 두 곳인 이유:
 *  - `pointerdown`은 **요소에만** 건다. HUD 버튼에서 시작된 터치까지 카메라가 먹으면 안 된다.
 *  - `pointermove`/`up`/`cancel`은 **문서에** 건다. 손가락이 캔버스 밖으로 나가도 드래그가 이어져야 하고,
 *    캔버스 밖에서 손을 떼도 포인터가 눌린 채로 남지 않아야 한다.
 *    (`setPointerCapture`로도 되지만 그건 브라우저 구현 편차가 크고, 문서 리스너면 충분하다.)
 */
export function createPointerGestures(
  el: HTMLElement,
  options: PointerGestureOptions = {},
): PointerGestures {
  const now = options.now ?? (() => performance.now())
  const tapSlopPx = options.tapSlopPx ?? TAP_SLOP_PX
  const tapMaxDurationMs = options.tapMaxDurationMs ?? TAP_MAX_DURATION_MS
  const shouldManageTouchAction = options.manageTouchAction ?? true

  const doc = el.ownerDocument
  const view = doc.defaultView

  const tapped = createSignal<[number, number]>()
  const panned = createSignal<[number, number]>()
  const aimed = createSignal<[number, number]>()
  const aimEnded = createSignal<[number, number]>()
  const pinched = createSignal<[number, number, number]>()
  const rotated = createSignal<[1 | -1]>()

  /** 눌려 있는 포인터. 삽입 순서가 유지되므로 핀치의 두 포인터 순서도 안정적이다. */
  const pointers = new Map<number, TrackedPointer>()
  let pinch: PinchTracker | null = null
  let twistRadians = 0

  const previousTouchAction = el.style.touchAction
  if (shouldManageTouchAction) el.style.touchAction = 'none'

  /* --- 포인터 집합 관리 --- */

  function cancelAllTapCandidates(): void {
    for (const [id, tracked] of pointers) {
      if (!tracked.isTapCandidate) continue
      pointers.set(id, { ...tracked, isTapCandidate: false })
    }
  }

  /**
   * 포인터 집합이 바뀔 때마다 핀치 기준을 다시 잡는다.
   *
   * 이 한 줄이 "핀치 중 한 손가락을 떼면 화면이 튀는" 고전 버그를 막는다 -
   * 남은 손가락의 위치는 이미 최신이므로 기준만 새로 잡으면 다음 델타가 정상값이 된다.
   */
  function resyncPinch(): void {
    const [a, b] = [...pointers.values()]
    if (a === undefined || b === undefined || pointers.size !== PINCH_POINTER_COUNT) {
      pinch = null
      twistRadians = 0
      return
    }

    pinch = { distance: Math.hypot(b.x - a.x, b.y - a.y), angle: Math.atan2(b.y - a.y, b.x - a.x) }
    twistRadians = 0
  }

  /** 위치를 갱신하고 슬롭 래치를 판정한다. 한 번 벗어난 포인터는 탭 후보로 돌아오지 않는다. */
  function advance(tracked: TrackedPointer, x: number, y: number): TrackedPointer {
    const travelled = Math.hypot(x - tracked.startX, y - tracked.startY)
    const isWithinSlop = travelled <= tapSlopPx

    return {
      ...tracked,
      x,
      y,
      isTapCandidate: tracked.isTapCandidate && isWithinSlop,
      isPanning: tracked.isPanning || !isWithinSlop,
    }
  }

  /* --- 이벤트 처리 --- */

  function handlePointerDown(ev: PointerEvent): void {
    // 텍스트 선택·캔버스 드래그·롱프레스 메뉴를 막는다. 게임 화면에서는 전부 방해 동작이다.
    if (ev.cancelable) ev.preventDefault()

    pointers.set(ev.pointerId, {
      id: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      startTimeMs: now(),
      x: ev.clientX,
      y: ev.clientY,
      isTapCandidate: true,
      isPanning: false,
    })

    // 두 번째 손가락이 닿는 순간 **모든** 포인터가 탭 자격을 잃는다.
    // 핀치를 끝내며 손가락을 떼는 것이 발사로 이어지면 그게 곧 탄환 손실이다.
    if (pointers.size > PAN_POINTER_COUNT) cancelAllTapCandidates()

    resyncPinch()
  }

  function handlePointerMove(ev: PointerEvent): void {
    const tracked = pointers.get(ev.pointerId)
    if (tracked === undefined) return

    const next = advance(tracked, ev.clientX, ev.clientY)
    pointers.set(ev.pointerId, next)

    if (pointers.size === PINCH_POINTER_COUNT) {
      updatePinch()
      return
    }

    // 세 손가락 이상은 아직 의미를 부여하지 않는다. 팬으로 해석하면 손바닥이 닿았을 때 화면이 날아간다.
    if (pointers.size !== PAN_POINTER_COUNT) return

    // 조준은 슬롭을 기다리지 않는다(`onAimMove` 주석). 팬은 여전히 슬롭 뒤부터다.
    aimed.emit(next.x, next.y)
    if (!next.isPanning) return

    panned.emit(next.x - tracked.x, next.y - tracked.y)
  }

  function updatePinch(): void {
    const [a, b] = [...pointers.values()]
    if (a === undefined || b === undefined) return

    const distance = Math.hypot(b.x - a.x, b.y - a.y)
    const angle = Math.atan2(b.y - a.y, b.x - a.x)
    const previous = pinch
    pinch = { distance, angle }

    // 두 손가락이 정확히 겹치면 배율이 0/∞가 된다. 기준만 갱신하고 넘어간다.
    if (previous === null || previous.distance <= 0 || distance <= 0) return

    const scaleDelta = distance / previous.distance
    if (scaleDelta !== NO_SCALE_CHANGE) {
      pinched.emit(scaleDelta, (a.x + b.x) / PINCH_POINTER_COUNT, (a.y + b.y) / PINCH_POINTER_COUNT)
    }

    accumulateTwist(shortestAngleDelta(previous.angle, angle))
  }

  /**
   * 트위스트를 누적해 임계 각도마다 회전 한 단계를 발행한다.
   *
   * 화면 좌표는 y가 아래로 증가하므로 각도 증가 = **시계 방향**이다.
   * 시계 방향 = +1로 두어 "손가락을 오른쪽으로 돌리면 방이 오른쪽으로 돈다"를 맞춘다.
   * (+1이 실제로 어느 방위인지는 `isoCamera`가 정한다 - 여기서는 부호 규약만 지킨다.)
   */
  function accumulateTwist(delta: number): void {
    if (!Number.isFinite(delta)) return

    twistRadians += delta

    while (Math.abs(twistRadians) >= TWIST_STEP_RADIANS) {
      const step: 1 | -1 = twistRadians > 0 ? 1 : -1
      twistRadians -= step * TWIST_STEP_RADIANS
      rotated.emit(step)
    }
  }

  function handlePointerUp(ev: PointerEvent): void {
    const tracked = pointers.get(ev.pointerId)
    if (tracked === undefined) return

    // `pointermove` 없이 곧바로 `pointerup`이 오는 경로가 있으므로 뗀 지점으로 한 번 더 판정한다.
    const settled = advance(tracked, ev.clientX, ev.clientY)
    pointers.delete(ev.pointerId)
    resyncPinch()

    if (!settled.isTapCandidate) {
      // 끌어서 겨누고 뗀 것. 손가락이 하나였을 때만이다 - 핀치를 끝내며 떼는 것은 조준이 아니다.
      if (settled.isPanning && pointers.size === 0) aimEnded.emit(settled.x, settled.y)
      return
    }
    if (now() - settled.startTimeMs > tapMaxDurationMs) return

    tapped.emit(settled.startX, settled.startY)
  }

  /**
   * 브라우저가 제스처를 가져갔거나(스크롤·시스템 UI) 손가락이 인식에서 빠졌다.
   * **절대 탭으로 처리하지 않는다.** `pointercancel` 누락은 이 계열에서 가장 흔한 버그이며,
   * 빠뜨리면 눌린 채로 남은 유령 포인터가 다음 팬을 화면 밖으로 날려 보낸다.
   */
  function handlePointerCancel(ev: PointerEvent): void {
    if (!pointers.delete(ev.pointerId)) return
    resyncPinch()
  }

  /** 창이 포커스를 잃으면 뗌 이벤트가 영영 오지 않을 수 있다. 유령 포인터를 남기지 않는다. */
  function handleWindowBlur(): void {
    pointers.clear()
    pinch = null
    twistRadians = 0
  }

  function handleWheel(ev: WheelEvent): void {
    // passive: false로 등록했으므로 막을 수 있다. 막지 않으면 페이지가 스크롤되거나 브라우저가 확대된다.
    if (ev.cancelable) ev.preventDefault()

    const scaleDelta = wheelScaleDelta(ev, el)
    if (scaleDelta === NO_SCALE_CHANGE) return

    pinched.emit(scaleDelta, ev.clientX, ev.clientY)
  }

  /** 롱프레스·우클릭 메뉴는 조준 중에 뜨면 그 자체가 조작 사고다. */
  function handleContextMenu(ev: MouseEvent): void {
    ev.preventDefault()
  }

  function handleKeyDown(ev: KeyboardEvent): void {
    // 누르고 있으면 초당 수십 번 회전 요청이 나간다. 한 번 누름 = 한 단계다.
    if (ev.repeat) return
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return
    if (isEditableTarget(ev.target)) return

    if (ev.code === ROTATE_KEY_COUNTERCLOCKWISE) rotated.emit(-1)
    else if (ev.code === ROTATE_KEY_CLOCKWISE) rotated.emit(1)
  }

  el.addEventListener('pointerdown', handlePointerDown)
  el.addEventListener('wheel', handleWheel, { passive: false })
  el.addEventListener('contextmenu', handleContextMenu)
  doc.addEventListener('pointermove', handlePointerMove)
  doc.addEventListener('pointerup', handlePointerUp)
  doc.addEventListener('pointercancel', handlePointerCancel)
  doc.addEventListener('keydown', handleKeyDown)
  view?.addEventListener('blur', handleWindowBlur)

  return {
    onTap: tapped.add,
    onPanDelta: panned.add,
    onAimMove: aimed.add,
    onAimEnd: aimEnded.add,
    onPinchZoom: pinched.add,
    onRotateRequest: rotated.add,

    dispose: () => {
      el.removeEventListener('pointerdown', handlePointerDown)
      el.removeEventListener('wheel', handleWheel)
      el.removeEventListener('contextmenu', handleContextMenu)
      doc.removeEventListener('pointermove', handlePointerMove)
      doc.removeEventListener('pointerup', handlePointerUp)
      doc.removeEventListener('pointercancel', handlePointerCancel)
      doc.removeEventListener('keydown', handleKeyDown)
      view?.removeEventListener('blur', handleWindowBlur)

      if (shouldManageTouchAction) el.style.touchAction = previousTouchAction

      pointers.clear()
      pinch = null
      tapped.clear()
      panned.clear()
      pinched.clear()
      rotated.clear()
    },
  }
}
